import { AppDispatch } from 'store';
import { INBOX_LIST_TAG, messageApi } from './messageApi';
import { tradeApi } from '../trade/tradeApi';
import type {
  NewMessagePayload,
  Message,
  Reaction,
  ReactionUpdatePayload,
} from 'types/interfaces/message';

/**
 * Refetch the inbox list and the per-type counts after a socket event.
 *
 * This replaced a cache patch keyed on `{ page: 1, pageSize: 25 }`, which
 * never matched the list page's actual query key (it also carries `archived`
 * and now `type`), so live list updates silently never landed. With typed
 * threads the row's subject, group and title are server-computed from the
 * message's subject, which a client patch cannot reproduce anyway, so the list
 * is invalidated and refetched (Asana 1219952262403623).
 */
export const invalidateInboxList = (dispatch: AppDispatch) => {
  dispatch(messageApi.util.invalidateTags([INBOX_LIST_TAG, 'InboxCounts']));
};

/** Refetch only the per-type counts (badge recount events). */
export const invalidateInboxCounts = (dispatch: AppDispatch) => {
  dispatch(messageApi.util.invalidateTags(['InboxCounts']));
};

/**
 * Update a specific conversation thread with a new message.
 * Used when viewing a conversation and receiving a new message.
 */
export const updateThreadWithNewMessage = (
  dispatch: AppDispatch,
  conversationId: string,
  payload: NewMessagePayload
) => {
  dispatch(
    messageApi.util.updateQueryData('getConversationThread', conversationId, (draft) => {
      // Convert payload to Message format
      const newMessage: Message = {
        id: payload.messageId,
        sender: {
          id: payload.sender.id,
          username: payload.sender.username,
          avatarUrl: payload.sender.avatarUrl,
          country: payload.sender.country,
        },
        content: payload.content,
        createdAt: payload.createdAt,
        isRead: false,
        isSystemMessage: payload.type === 'SYSTEM',
        images: payload.images,
      };
      draft.messages.push(newMessage);
      // Update image count if images were included
      if (payload.images?.length && draft.imageCount !== undefined) {
        draft.imageCount += payload.images.length;
      }
    })
  );
};

/**
 * Update the unread count badge.
 */
export const updateUnreadCount = (dispatch: AppDispatch, count: number) => {
  dispatch(
    messageApi.util.updateQueryData('getUnreadCount', undefined, (draft) => {
      draft.count = count;
    })
  );
};

/**
 * Update the inbound trade count badge.
 */
export const updateInboundTradeCount = (
  dispatch: AppDispatch,
  count: number,
  hasOverdue: boolean
) => {
  dispatch(
    tradeApi.util.updateQueryData('getInboundTradeCount', undefined, (draft) => {
      draft.count = count;
      draft.hasOverdueTrades = hasOverdue;
    })
  );
};

/**
 * Update message read status in a conversation thread.
 * Called when sender receives read receipt from recipient.
 */
export const updateMessageReadStatus = (
  dispatch: AppDispatch,
  conversationId: string,
  messageIds: string[],
  readAt: string
) => {
  dispatch(
    messageApi.util.updateQueryData('getConversationThread', conversationId, (draft) => {
      draft.messages.forEach((msg) => {
        if (messageIds.includes(msg.id)) {
          msg.isRead = true;
          // Add readAt timestamp for read receipt display
          (msg as Message & { readAt?: string }).readAt = readAt;
        }
      });
    })
  );
};

/**
 * Update reactions on a specific message in the conversation thread cache.
 * Used by WebSocket reaction:update events from the other participant.
 */
export const updateMessageReactions = (
  dispatch: AppDispatch,
  conversationId: string,
  messageId: string,
  reactions: Reaction[]
) => {
  dispatch(
    messageApi.util.updateQueryData('getConversationThread', conversationId, (draft) => {
      const msg = draft.messages.find((m) => m.id === messageId);
      if (msg) {
        msg.reactions = reactions;
      }
    })
  );
};

/**
 * Handle real-time reaction update from WebSocket.
 */
export const handleReactionUpdate = (
  dispatch: AppDispatch,
  payload: ReactionUpdatePayload
) => {
  updateMessageReactions(
    dispatch,
    payload.conversationId,
    payload.messageId,
    payload.reactions
  );
};
